import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
  Skeleton,
  Button,
} from "@asm/ui";
import {
  AlertTriangle,
  Shield,
  ShieldAlert,
  ShieldOff,
  Server,
  Globe,
  Clock,
  ExternalLink,
  RefreshCw,
  Eye,
  EyeOff,
  Network,
  Cloud,
} from "lucide-react";
import { riskApi } from "@/lib/api";

// Cloud provider icons mapping
const CLOUD_PROVIDER_ICONS: Record<string, { icon: string; name: string }> = {
  aws: { icon: "/icons/aws.svg", name: "AWS" },
  amazon: { icon: "/icons/aws.svg", name: "AWS" },
  azure: { icon: "/icons/azure.svg", name: "Azure" },
  microsoft: { icon: "/icons/azure.svg", name: "Azure" },
  gcp: { icon: "/icons/gcp.svg", name: "GCP" },
  google: { icon: "/icons/gcp.svg", name: "GCP" },
  cloudflare: { icon: "/icons/cloudflare.svg", name: "Cloudflare" },
  digitalocean: { icon: "/icons/digitalocean.svg", name: "DigitalOcean" },
  linode: { icon: "/icons/linode.svg", name: "Linode" },
  akamai: { icon: "/icons/akamai.svg", name: "Akamai" },
};

// AWS service-specific icons
const AWS_SERVICE_ICONS: Record<string, { icon: string; name: string }> = {
  cloudfront: { icon: "/icons/aws-cloudfront.svg", name: "AWS CloudFront" },
  ec2: { icon: "/icons/aws-ec2.svg", name: "AWS EC2" },
  s3: { icon: "/icons/aws-s3.svg", name: "AWS S3" },
  lambda: { icon: "/icons/aws-lambda.svg", name: "AWS Lambda" },
  elb: { icon: "/icons/aws-elb.svg", name: "AWS ELB" },
};

function getCloudInfo(
  provider: string | null | undefined,
  service: string | null | undefined
): { icon: string; name: string } | null {
  if (!provider) return null;
  const providerKey = provider.toLowerCase().replace(/[^a-z]/g, "");

  if (providerKey === "aws" || providerKey === "amazon") {
    if (service) {
      const serviceKey = service.toLowerCase().replace(/[^a-z]/g, "");
      if (AWS_SERVICE_ICONS[serviceKey]) return AWS_SERVICE_ICONS[serviceKey];
    }
    return CLOUD_PROVIDER_ICONS.aws;
  }

  for (const [k, v] of Object.entries(CLOUD_PROVIDER_ICONS)) {
    if (providerKey.includes(k)) return v;
  }
  return null;
}

// Simple relative time formatter
function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) return `${diffDays}d ago`;
  if (diffHours > 0) return `${diffHours}h ago`;
  if (diffMins > 0) return `${diffMins}m ago`;
  return "just now";
}

function getRiskBadgeVariant(risk: string) {
  switch (risk.toLowerCase()) {
    case "high":
    case "critical":
      return "destructive";
    case "medium":
      return "warning" as const;
    case "low":
      return "secondary";
    default:
      return "outline";
  }
}

export default function RiskDashboard() {
  const {
    data: risks,
    isLoading,
    refetch,
    error,
  } = useQuery({
    queryKey: ["risk-summary"],
    queryFn: () => riskApi.getRiskSummary({ limit: 10 }),
    staleTime: 30_000,
  });

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <ShieldOff className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold">Unable to load risks</h3>
        <p className="text-muted-foreground mt-1">
          {error instanceof Error ? error.message : "An error occurred"}
        </p>
        <Button onClick={() => refetch()} variant="outline" className="mt-4">
          <RefreshCw className="h-4 w-4 mr-2" />
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid gap-4 md:grid-cols-5">
        <StatCard
          title="Takeover Risks"
          value={risks?.stats.total_takeover_risks}
          icon={<ShieldAlert className="h-4 w-4 text-red-500" />}
          loading={isLoading}
          severity="high"
        />
        <StatCard
          title="Exposed Services"
          value={risks?.stats.total_exposed_services}
          icon={<Network className="h-4 w-4 text-orange-500" />}
          loading={isLoading}
          severity="medium"
        />
        <StatCard
          title="Shadow IT"
          value={risks?.stats.total_shadow_assets}
          icon={<EyeOff className="h-4 w-4 text-yellow-500" />}
          loading={isLoading}
          severity="medium"
        />
        <StatCard
          title="Stale Assets"
          value={risks?.stats.total_stale_assets}
          icon={<Clock className="h-4 w-4 text-blue-500" />}
          loading={isLoading}
          severity="low"
        />
        <StatCard
          title="Critical CVEs"
          value={risks?.stats.total_critical_cves}
          icon={<Shield className="h-4 w-4 text-purple-500" />}
          loading={isLoading}
          severity="critical"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Takeover Risks */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <ShieldAlert className="h-5 w-5 text-red-500" />
                  Takeover Candidates
                </CardTitle>
                <CardDescription>
                  Subdomains at risk of takeover
                </CardDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : risks?.takeover_risks.length === 0 ? (
              <EmptyState
                icon={<Shield className="h-8 w-8" />}
                message="No takeover risks detected"
              />
            ) : (
              <div className="space-y-3">
                {risks?.takeover_risks.map((risk) => (
                  <Link key={risk.asset_id} to={`/assets/${risk.asset_id}`}>
                    <div className="flex items-start justify-between p-3 rounded-lg border hover:bg-accent/50 transition-colors group">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4 text-blue-500" />
                          <span className="font-mono text-sm">{risk.fqdn}</span>
                        </div>
                        {risk.takeover_reason && (
                          <p className="text-xs text-muted-foreground line-clamp-1">
                            {risk.takeover_reason}
                          </p>
                        )}
                        {risk.cname_target && (
                          <p className="text-xs text-muted-foreground">
                            CNAME: {risk.cname_target}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={getRiskBadgeVariant(risk.takeover_risk)}>
                          {risk.takeover_risk}
                        </Badge>
                        <ExternalLink className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Exposed Services */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Network className="h-5 w-5 text-orange-500" />
                  Exposed Services
                </CardTitle>
                <CardDescription>
                  Sensitive services exposed to internet
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : risks?.exposed_services.length === 0 ? (
              <EmptyState
                icon={<Server className="h-8 w-8" />}
                message="No sensitive services exposed"
              />
            ) : (
              <div className="space-y-3">
                {risks?.exposed_services.map((service) => {
                  const cloudInfo = getCloudInfo(service.cloud_provider, service.cloud_region);
                  return (
                    <Link key={service.asset_id} to={`/assets/${service.asset_id}`}>
                      <div className="flex items-start justify-between p-3 rounded-lg border hover:bg-accent/50 transition-colors group">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            {cloudInfo ? (
                              <img src={cloudInfo.icon} alt={cloudInfo.name} className="h-4 w-4" />
                            ) : service.is_cloud ? (
                              <Cloud className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <Server className="h-4 w-4 text-green-500" />
                            )}
                            <span className="font-mono text-sm">
                              {service.ip_address}:{service.port}
                            </span>
                            {service.service_name && (
                              <Badge variant="outline" className="text-xs">
                                {service.service_name}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {cloudInfo && (
                              <span className="text-xs text-blue-500">{cloudInfo.name}</span>
                            )}
                            <p className="text-xs text-muted-foreground line-clamp-1">
                              {service.risk_reason}
                            </p>
                          </div>
                        </div>
                        <ExternalLink className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Shadow IT */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <EyeOff className="h-5 w-5 text-yellow-500" />
                  Shadow IT
                </CardTitle>
                <CardDescription>
                  Unknown or rogue assets discovered
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : risks?.shadow_assets.length === 0 ? (
              <EmptyState
                icon={<Eye className="h-8 w-8" />}
                message="No shadow assets found"
              />
            ) : (
              <div className="space-y-2">
                {risks?.shadow_assets.map((asset) => {
                  const cloudInfo = (asset.asset_type === "ip" || asset.asset_type === "port")
                    ? getCloudInfo(asset.cloud_provider, asset.cloud_region)
                    : null;
                  return (
                    <Link key={asset.id} to={`/assets/${asset.id}`}>
                      <div className="flex items-center justify-between p-3 rounded-lg border hover:bg-accent/50 transition-colors group">
                        <div className="flex items-center gap-3">
                          {cloudInfo ? (
                            <img src={cloudInfo.icon} alt={cloudInfo.name} className="h-4 w-4" />
                          ) : asset.is_cloud ? (
                            <Cloud className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <AssetTypeIcon type={asset.asset_type} />
                          )}
                          <div>
                            <span className="font-mono text-sm">{asset.identifier}</span>
                            <p className="text-xs text-muted-foreground">
                              <span className="capitalize">{asset.asset_type === "ip" ? "IP" : asset.asset_type}</span>
                              {cloudInfo && <span className="text-blue-500 ml-1">· {cloudInfo.name}</span>}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge
                            variant={asset.visibility === "rogue" ? "destructive" : "secondary"}
                          >
                            {asset.visibility || "unknown"}
                          </Badge>
                          <ExternalLink className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stale Assets */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-blue-500" />
                  Stale Assets
                </CardTitle>
                <CardDescription>
                  Assets not seen in 30+ days
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : risks?.stale_assets.length === 0 ? (
              <EmptyState
                icon={<Clock className="h-8 w-8" />}
                message="No stale assets"
              />
            ) : (
              <div className="space-y-2">
                {risks?.stale_assets.map((asset) => {
                  const cloudInfo = (asset.asset_type === "ip" || asset.asset_type === "port")
                    ? getCloudInfo(asset.cloud_provider, asset.cloud_region)
                    : null;
                  return (
                    <Link key={asset.id} to={`/assets/${asset.id}`}>
                      <div className="flex items-center justify-between p-3 rounded-lg border hover:bg-accent/50 transition-colors group">
                        <div className="flex items-center gap-3">
                          {cloudInfo ? (
                            <img src={cloudInfo.icon} alt={cloudInfo.name} className="h-4 w-4" />
                          ) : asset.is_cloud ? (
                            <Cloud className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <AssetTypeIcon type={asset.asset_type} />
                          )}
                          <div>
                            <span className="font-mono text-sm">{asset.identifier}</span>
                            <p className="text-xs text-muted-foreground">
                              <span className="capitalize">{asset.asset_type === "ip" ? "IP" : asset.asset_type}</span>
                              {cloudInfo && <span className="text-blue-500 ml-1">· {cloudInfo.name}</span>}
                              <span className="ml-1">· Last seen {formatRelativeTime(new Date(asset.last_seen_at))}</span>
                            </p>
                          </div>
                        </div>
                        <ExternalLink className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function StatCard({
  title,
  value,
  icon,
  loading,
  severity,
}: {
  title: string;
  value?: number;
  icon: React.ReactNode;
  loading: boolean;
  severity: "critical" | "high" | "medium" | "low";
}) {
  const getValueColor = () => {
    if (!value || value === 0) return "text-muted-foreground";
    switch (severity) {
      case "critical":
        return "text-red-600 dark:text-red-400";
      case "high":
        return "text-orange-600 dark:text-orange-400";
      case "medium":
        return "text-yellow-600 dark:text-yellow-400";
      case "low":
        return "text-blue-600 dark:text-blue-400";
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-8 w-16" />
        ) : (
          <div className={`text-2xl font-bold ${getValueColor()}`}>
            {value || 0}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AssetTypeIcon({ type }: { type: string }) {
  switch (type) {
    case "subdomain":
      return <Globe className="h-4 w-4 text-blue-500" />;
    case "ip":
      return <Server className="h-4 w-4 text-green-500" />;
    case "port":
      return <Network className="h-4 w-4 text-purple-500" />;
    default:
      return <AlertTriangle className="h-4 w-4 text-muted-foreground" />;
  }
}

function EmptyState({ icon, message }: { icon: React.ReactNode; message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
      <div className="opacity-50 mb-2">{icon}</div>
      <p className="text-sm">{message}</p>
    </div>
  );
}
